import { useKindeFeatureFlags, useTranslation } from "@next-platform/client-common-core";
import { CapabilityKey, SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { Button, ButtonSize, ButtonVariant, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import BotListToolbarV2 from "@/components/BotListToolbarV2";
import Trans from "@/components/Trans/Trans";
import { FilterKeys, FilterOptions, SortOptions } from "@/components/type";
import { FEATURE_FLAGS, ROUTES } from "@/constants";
import getUserPlan from "@/features/user/utils/getUserPlan";
import { useFeatureFlag, useHasOtherCreationFeatures } from "@/hooks";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import UnlockCustomVoicesModal from "@/pages/CreateCharacter/v2/components/UnlockCustomVoicesModal";
import { resolveCreationTab } from "@/pages/Creations/creationTabMetaPageId";
import { getLayoutOptions } from "@/pages/Creations/layoutoptions";
import useAuth from "@/services/useAuth";
import { CreationPaths } from "@/types/common";

export type OutletContextType = [string, { key: FilterKeys; value: FilterOptions }, SortOptions];

const renderPathLabel = (path: CreationPaths) => {
  return {
    [CreationPaths.CHATBOTS]: <Trans i18nKey="myCreation:tab.chatbots" />,
    [CreationPaths.GROUPS]: <Trans i18nKey="myCreation:tab.groupchats" />,
    [CreationPaths.LOREBOOKS]: <Trans i18nKey="myCreation:tab.lorebooks" />,
    [CreationPaths.RULEBOOKS]: <Trans i18nKey="myCreation:tab.rulebooks" />,
    [CreationPaths.VOICES]: <Trans i18nKey="myCreation:tab.voices" />,
  }[path];
};

// TODO(igor-nextday): [NX-7407] move user-facing label to Namespace.CreatorAnalytics i18n keys.
const CREATOR_ANALYTICS_BUTTON_LABEL = "Creator Analytics";

const CREATION_CAPABILITIES = [
  SubCapabilityKey.LorebookManage,
  SubCapabilityKey.GroupChatManage,
  SubCapabilityKey.TtsManageCustomVoice,
  SubCapabilityKey.RulebookManage,
];

type CreationNavPathFlags = {
  isLorebookManageAllowed: boolean;
  isRulebookManageAllowed: boolean;
  isGroupChatManageAllowed: boolean;
  hasVoiceTab: boolean;
};

const buildCreationNavPaths = ({ isLorebookManageAllowed, isRulebookManageAllowed, isGroupChatManageAllowed, hasVoiceTab }: CreationNavPathFlags) => {
  const navPaths = [CreationPaths.CHATBOTS];
  if (isLorebookManageAllowed) navPaths.push(CreationPaths.LOREBOOKS);
  if (isRulebookManageAllowed) navPaths.push(CreationPaths.RULEBOOKS);
  if (isGroupChatManageAllowed) navPaths.push(CreationPaths.GROUPS);
  if (hasVoiceTab) navPaths.push(CreationPaths.VOICES);
  return navPaths;
};

function getNavLinkClassName({ isActive }: { isActive: boolean }) {
  const color = `text-${isActive ? "blue-12" : "gray-11"}`;
  let cls = `font-[inherit] text-decoration-skip-ink-none text-underline-position-from-font underline-offset-[16px] decoration-[1.5px] ${color}`;
  if (isActive) cls += " underline";
  return cls;
}

const CreationLayout = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { hasAnyCreationFeatures } = useHasOtherCreationFeatures();
  const { pagePath } = useLocalizedLocation();
  const [stateSearch, setStateSearch] = useState("");
  const [stateFilter, setStateFilter] = useState<OutletContextType[1]>({ key: FilterKeys.ALL, value: FilterOptions.ALL });
  const [stateSort, setStateSort] = useState(SortOptions.LATEST);
  const { isUserPremium } = getUserPlan();
  const { hasAccessToTTS } = useKindeFeatureFlags();
  const hasCustomVoicesAccess = isUserPremium && hasAccessToTTS;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- pre-existing naming, not touched by current PR
  const [stateShowUnlockVoicesModal, setStateShowUnlockVoicesModal] = useState(false);

  const handleTabClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      const path = e.currentTarget.dataset.path;
      if (path === CreationPaths.VOICES && !hasCustomVoicesAccess) {
        e.preventDefault();
        setStateShowUnlockVoicesModal(true);
      }
    },
    [hasCustomVoicesAccess],
  );
  const { hasFeatureFlag: hasVoice } = useFeatureFlag(FEATURE_FLAGS.TTS);
  const { hasFeatureFlag: hasCreatorAnalytics } = useFeatureFlag(FEATURE_FLAGS.CREATOR_ANALYTICS);
  const [isLorebookManageAllowed, isGroupChatManageAllowed, isTtsManageCustomVoiceAllowed, isRulebookManageAllowed] =
    useApplicationCapabilitiesAllowed(CREATION_CAPABILITIES);
  const isCreatorAnalyticsAllowed = useApplicationCapabilitiesAllowed(CapabilityKey.CreatorAnalytics);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const creationTab = resolveCreationTab({ pagePath });
  const layoutOptions = useMemo(() => getLayoutOptions(t), [t]);

  const handleNavigateToAnalytics = useCallback(() => {
    navigate(ROUTES.creatorAnalytics);
  }, [navigate]);

  useEffect(() => {
    setStateSearch("");
    setStateFilter({ key: FilterKeys.ALL, value: FilterOptions.ALL });
    setStateSort(SortOptions.LATEST);
  }, [creationTab]);

  const { sortOptions, hideFilter, searchPlaceholder } = layoutOptions[creationTab];

  const outletContext = useMemo<OutletContextType>(() => [stateSearch, stateFilter, stateSort], [stateSearch, stateFilter, stateSort]);

  const handleFilterChange = useCallback((key: FilterKeys, value?: FilterOptions) => {
    setStateFilter({ key, value: value ?? FilterOptions.ALL });
  }, []);

  if (isLoading || !isAuthenticated) {
    return <Outlet context={outletContext} />;
  }

  const navPaths = buildCreationNavPaths({
    isLorebookManageAllowed,
    isRulebookManageAllowed,
    isGroupChatManageAllowed,
    hasVoiceTab: hasVoice && isTtsManageCustomVoiceAllowed,
  });

  return (
    <>
      <Text variant={TextVariants.HEADING_5_MEDIUM}>
        {hasAnyCreationFeatures ? <Trans i18nKey="myCreation:page.title" /> : <Trans i18nKey="myCreation:page.chatbots" />}
      </Text>
      <div className="my-lg">
        <BotListToolbarV2
          searchQuery={stateSearch}
          onSearchChange={setStateSearch}
          filterBy={stateFilter.value}
          onFilterChange={handleFilterChange}
          sortBy={stateSort}
          onSortChange={setStateSort}
          sortOptions={sortOptions}
          hideFilter={hideFilter}
          contentType={creationTab as CreationPaths}
          searchPlaceholder={searchPlaceholder}
        />
      </div>
      <menu>
        <ul className="flex flex-wrap items-center gap-2 min-h-11">
          {navPaths.map((path) => (
            <li key={path} className="font-roboto text-label-md font-regular cursor-pointer text-gray-11">
              <NavLink className={getNavLinkClassName} to={path} data-path={path} onClick={handleTabClick} data-testid={`creation-tab-${path}`}>
                {renderPathLabel(path)}
              </NavLink>
            </li>
          ))}
          {hasCreatorAnalytics && isCreatorAnalyticsAllowed && creationTab === CreationPaths.CHATBOTS ? (
            <li className="ml-auto">
              <Button
                variant={ButtonVariant.CONTAINED}
                size={ButtonSize.SMALL}
                onClick={handleNavigateToAnalytics}
                className="max-w-full whitespace-nowrap"
              >
                {CREATOR_ANALYTICS_BUTTON_LABEL}
              </Button>
            </li>
          ) : null}
        </ul>
      </menu>
      <Outlet context={outletContext} />
      <UnlockCustomVoicesModal isOpen={stateShowUnlockVoicesModal} onClose={setStateShowUnlockVoicesModal} />
    </>
  );
};
export default CreationLayout;
